export const FEATURE_FLAG_KEYS = [
  "ordersReaderParser",
  "speechNormalization",
  "ordersReaderModule",
  "speechOutput",
  "preflight",
  "delivery",
  "trace",
  "gridRecognition",
  "automaticOrders",
  "estimatedGrids",
  "eyesUp",
  "questions",
  "diagnostics",
  "appUpdates",
  "phaseWalk",
  "radioProcedure",
  "battleProcedure",
  "tacticalSimulation",
] as const;

export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];
export type FeatureFlagSnapshot = Readonly<Record<FeatureFlagKey, boolean>>;

export const DEFAULT_FEATURE_FLAGS: FeatureFlagSnapshot = Object.freeze({
  ordersReaderParser: true,
  speechNormalization: true,
  ordersReaderModule: true,
  speechOutput: true,
  preflight: true,
  delivery: true,
  trace: true,
  gridRecognition: true,
  automaticOrders: false,
  // Ruling 31, 18 September 2026. Off: with it off a low-confidence grid
  // resolves nothing, exactly as before. On, an estimate may fill a blank —
  // and every use of it is announced, because an estimate that reaches the
  // trainee unmarked teaches him a false grid.
  estimatedGrids: false,
  eyesUp: false,
  questions: false,
  diagnostics: false,
  // R1 Step 15. On: the offline install is how the tool reaches the field, and
  // acceptance requires an offline run after first load.
  appUpdates: true,
  // R1, doc 04 section 10.3. Off: the mechanism is built and tested, and
  // nothing in the delivery path reaches it while this is false.
  phaseWalk: false,
  radioProcedure: false,
  battleProcedure: true,
  tacticalSimulation: false,
});

export interface FeatureFlagService {
  isEnabled(key: FeatureFlagKey): boolean;
  snapshot(): FeatureFlagSnapshot;
}

export function createFeatureFlagService(
  overrides: Partial<Record<FeatureFlagKey, boolean>> = {},
): FeatureFlagService {
  const flags: FeatureFlagSnapshot = Object.freeze({
    ...DEFAULT_FEATURE_FLAGS,
    ...overrides,
  });

  return {
    isEnabled: (key) => flags[key],
    snapshot: () => ({ ...flags }),
  };
}
